import type { JSX } from 'react';
import { Box, Card, CardActionArea, Typography } from '@mui/material';
import { CornerRibbon } from '../components';
import { Svgs } from '../assets/svg';
import { logoGcSmall, logo3NSmall } from '../assets/png';
import { ProductTypeEnum } from '../models/PricebookData';
import { useDeviceType } from '../hooks/useDeviceType';
import { useCancelContractToast } from '../components/CancelContract';

interface ProductCardProps {
  bullets: string[];
  art: JSX.Element;
  product: ProductTypeEnum;
  setSelectedProduct: React.Dispatch<React.SetStateAction<ProductTypeEnum | undefined>>;
  selected: boolean;
  recommended: boolean;
  activeContract: boolean | undefined;
}

const ProductCard: React.FC<ProductCardProps> = ({
  bullets,
  art,
  product,
  setSelectedProduct,
  selected,
  recommended,
  activeContract,
}) => {
  const { isMobile, isTablet } = useDeviceType();
  const cancelContractToast = useCancelContractToast();

  const handleProductSelect = () => {
    if (activeContract === true) {
      return;
    }
    if (activeContract === false) {
      cancelContractToast();
      return;
    } else if (selected) {
      setSelectedProduct(undefined);
      return;
    }
    setSelectedProduct(product);
  };

  const isDisabled = Boolean(activeContract);
  const isChecked = selected || isDisabled;
  const selectionClassName = [
    isChecked ? 'pricebook-selected' : 'pricebook-unselected',
    isDisabled ? 'pricebook-disabled' : '',
    'pricebook-shadow',
    'product-card',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box sx={{ width: isMobile || isTablet ? '100%' : '335px', position: 'relative' }}>
      {recommended && <CornerRibbon msg='Recommended' position='bottom-right' />}

      <Card variant='pricebookPrimary' className={selectionClassName} sx={{ minHeight: '260px' }}>
        <CardActionArea
          onClick={handleProductSelect}
          disableRipple={isDisabled}
          sx={{ height: '100%', cursor: isDisabled ? 'default' : 'pointer' }}>
          <Box className='pricebook-card-content'>
            <Box
              sx={{
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {art}
            </Box>

            {(product === ProductTypeEnum.GuidedChoice || product === ProductTypeEnum.Nickels) && (
              <Box mt='20px' mb='10px' display='flex' justifyContent='center'>
                <img
                  src={product === ProductTypeEnum.GuidedChoice ? logoGcSmall : logo3NSmall}
                  alt={product === ProductTypeEnum.GuidedChoice ? 'GuidedChoice' : '3Nickels'}
                  style={{ height: '40px', objectFit: 'contain' }}
                />
              </Box>
            )}

            <Box mt='12px' width='100%' className='pricebook-bullets'>
              {bullets.map((bullet, index) => (
                <Box key={index} display='flex' gap='8px'>
                  <Typography variant='p12' color='info.main' sx={{ lineHeight: 1.5 }}>
                    •
                  </Typography>
                  <Typography variant='p12' color='info.main' sx={{ lineHeight: 1.5 }}>
                    {bullet}
                  </Typography>
                </Box>
              ))}
            </Box>

            {isChecked && (
              <Box className='pricebook-card-checkmark'>
                {isDisabled ? (
                  <Svgs.IconCheckmarkCustomGray />
                ) : (
                  <Svgs.IconCheckmarkCustomPrimary />
                )}
              </Box>
            )}
          </Box>
        </CardActionArea>
      </Card>
    </Box>
  );
};

export default ProductCard;
